import { Calendar, IndianRupee } from 'lucide-react'
import { Avatar } from '@/shared/components/ui/Avatar'
import { Badge } from '@/shared/components/ui/Badge'
import { formatINR } from '@/shared/utils/currency'
import type { TimelineDeal } from './types'

const SCOPES: Array<{ value: 'all' | 'mine' | 'following'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'mine', label: 'My Deals' },
  { value: 'following', label: 'Following' },
]

interface Props {
  deals: TimelineDeal[]
  selectedId: string | null
  onSelect: (id: string) => void
  scope: 'all' | 'mine' | 'following'
  onScopeChange: (scope: 'all' | 'mine' | 'following') => void
  counts: { all: number; mine: number; following: number }
  stageTone: (stage: string) => string
  loading: boolean
}

export function DealsColumn({
  deals,
  selectedId,
  onSelect,
  scope,
  onScopeChange,
  counts,
  stageTone,
  loading,
}: Props) {
  return (
    <aside className="flex w-full shrink-0 flex-col rounded-2xl border border-border/60 bg-card h-full self-stretch lg:w-[300px]">
      <div className="border-b border-border/50 px-4 py-3">
        <h2 className="text-sm font-bold text-foreground">Deals</h2>
      </div>
      <div className="flex gap-1 px-3 py-2">
        {SCOPES.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => onScopeChange(s.value)}
            className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors ${
              scope === s.value
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            {s.label} ({s.value === 'all' ? counts.all : s.value === 'mine' ? counts.mine : counts.following})
          </button>
        ))}
      </div>
      <div className="flex-1 min-h-0 space-y-1 overflow-y-auto px-2 pb-2">
        {loading && deals.length === 0 && (
          <p className="px-3 py-6 text-center text-xs text-muted-foreground">Loading deals…</p>
        )}
        {!loading && deals.length === 0 && (
          <p className="px-3 py-6 text-center text-xs text-muted-foreground">
            No deal matches these filters.
          </p>
        )}
        {deals.map((deal) => {
          const active = deal.id === selectedId
          return (
            <button
              key={deal.id}
              type="button"
              onClick={() => onSelect(deal.id)}
              className={`w-full rounded-xl border px-3 py-2.5 text-left transition-colors ${
                active
                  ? 'border-primary/50 bg-primary/5'
                  : 'border-transparent hover:border-border/60 hover:bg-muted/60'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-foreground">{deal.title}</p>
                  <Badge tone={stageTone(deal.stage)} size="sm" className="mt-1">
                    {deal.stage}
                  </Badge>
                  <p className="mt-1 truncate text-xs text-muted-foreground">{deal.client}</p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1 font-semibold text-foreground/80">
                      <IndianRupee className="h-3 w-3" />
                      {formatINR(deal.value)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {deal.closeDate || 'No close date'}
                    </span>
                  </div>
                </div>
                {deal.assignedToUserId && (
                  <div className="flex shrink-0 flex-col items-center gap-1">
                    <Avatar name={deal.owner || 'Unassigned'} size={26} />
                    <span className="max-w-[64px] truncate text-[10px] text-muted-foreground">
                      {(deal.owner || '').split(' ')[0]}
                    </span>
                  </div>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </aside>
  )
}
